/**
 * MAIN-845: what the menu page's import banner shows.
 *
 * The banner used to show only the most recent import. An earlier import that
 * was still waiting for review lost its only link from the menu page as soon
 * as a newer import started, failed or finished — in QA a failed EZ Cater
 * import hid a PDF import with 12 items still to review, reachable only by
 * typing its URL.
 */

const SOURCE_LABEL = {
  Pdf: "PDF import",
  EzCaterUrl: "EZ Cater import",
};

/** createdDate arrives as epoch milliseconds in a string, or as an ISO date. */
function toTime(value) {
  if (value === null || value === undefined || value === "") return 0;
  const asNumber = Number(value);
  if (Number.isFinite(asNumber)) return asNumber;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * True while a proposal still needs the caterer: not yet decided, edited but
 * not accepted, or accepted but not yet live. Rejected and applied items are
 * settled.
 */
export function isProposalOpen(proposal) {
  if (!proposal) return false;
  if (proposal.publishedMenuId) return false;
  // Older queries selected only _id; treat an unknown status as open so the
  // job is never hidden for lack of data.
  if (!proposal.reviewStatus) return true;
  return proposal.reviewStatus !== "Rejected";
}

/**
 * { failedJob, pendingReviewJobs, latestJobId } for a caterer's import jobs.
 *
 * Only the most recent import's failure is shown: a newer import supersedes an
 * older failure. Every import that is ready for review and still has an open
 * item is listed, newest first, whatever happened to later imports.
 */
export function getMenuImportBannerState(jobs = []) {
  const ordered = (jobs || [])
    .filter(Boolean)
    .map((job, index) => ({ job, index }))
    // Newest first; the server's order breaks ties.
    .sort((a, b) => toTime(b.job.createdDate) - toTime(a.job.createdDate) || a.index - b.index)
    .map(({ job }) => job);
  const latest = ordered[0] || null;

  return {
    failedJob: latest?.status === "FAILED" ? latest : null,
    // MAIN-879: imports still being read, shown with live progress.
    inProgressJobs: ordered.filter((job) => job.status === "QUEUED" || job.status === "PROCESSING"),
    pendingReviewJobs: ordered.filter(
      (job) => job.status === "NEEDS_REVIEW" && (job.proposals || []).some(isProposalOpen),
    ),
    latestJobId: latest?._id ?? null,
  };
}

/**
 * MAIN-879: an import's counters, from its proposals — the same definitions
 * the review screen uses. "remaining" is what still needs the caterer.
 */
export function countImportItems(job) {
  const proposals = job?.proposals || [];
  const published = proposals.filter((p) => p?.publishedMenuId).length;
  return {
    total: proposals.length,
    published,
    remaining: proposals.filter(isProposalOpen).length,
  };
}

const items = (n) => `${n} item${n === 1 ? "" : "s"}`;

/**
 * The banner sentence for one import that is waiting for review. MAIN-879:
 * it counts what is LEFT, not everything ever imported — "43 items" used to
 * stay 43 after two of them had gone live.
 */
export function describePendingReview(job, latestJobId) {
  const { total, published, remaining } = countImportItems(job);
  const progress = published ? ` ${published} of ${total} already published.` : "";
  if (job?._id === latestJobId) {
    return `${items(remaining)} from your last import still to review.${progress} Nothing else is live until you publish.`;
  }
  const source = SOURCE_LABEL[job?.sourceType] || "import";
  const time = toTime(job?.createdDate);
  const when = time
    ? ` on ${new Date(time).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
    : "";
  return `${items(remaining)} from your ${source}${when} still to review.${progress} Nothing else is live until you publish.`;
}
